import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {isAccountUntracked} from 'src/modules/profile/account/services/account-page-slice';
import {
    selectAccountLicense,
} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Body, Cell, Head, Table} from '@pb/kit-component-table';
import Typography from '@pb/kit-component-typography';

import {
    DealCloudRow,
    HubspotRow,
    MSDynamicsRow,
    PitchBookRow,
    SalesforceRow,
} from './sections';

import type {CrmType} from 'src/openapi/admin/models/CrmType';


export type CrmComponentProps = {
    accountId: number,
    licenseId: number,
    currentCrm: CrmType | null,
    runSaveChanges: boolean,
    runDiscardChanges: boolean,

    onChangeActiveCrm: (id: CrmType | null) => void,
    onChangesSaved: () => void,
    onChangesDiscarded: () => void,
    onActionDisabled: (disabled: boolean) => void,
    editDisabled?: boolean,
};

export const CrmTab = () => {
    const queryParams = useParams<{licenseId: string, accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const licenseId = queryParams.licenseId ? +queryParams.licenseId : -1;

    const {isFutureDate} = useDateTime();
    const {hasAccess, canEditCrm} = useAdminAccessRight();

    const isUntracked = useSelector(isAccountUntracked);
    const license = useSelector(selectAccountLicense);

    const {accountSubmit} = useEventTracking();

    const [currentCrmAccordion, setCurrentCrmAccordion] = useState<CrmType | null>(null);
    const [saveBusy, setSaveBusy] = useState(false);

    const [runSaveChanges, setRunSaveChanges] = useState(false);
    const [runDiscardChanges, setRunDiscardChanges] = useState(false);
    const [isActionDisabled, setActionDisabled] = useState(true);

    useEffect(() => {
        setCurrentCrmAccordion(null);
    }, [license?.licenseId]);

    const onChangesSaved = useCallback(() => {
        setRunSaveChanges(false);
        setSaveBusy(false);
    }, []);

    const isExpiredLicense = license
        ? !isFutureDate(license.startDate) && !license.isActive
        : false;

    if (!hasAccess(ACCESS_RIGHTS.CRM_SCREEN_VIEW)) {
        return <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to this page"/>;
    }

    if (isUntracked) {
        return (
            <ErrorPage
                handler="/"
                title="Access Denied"
                subtitle="CRM Configuration is disabled for untracked accounts"
            />
        );
    }

    const canEdit = canEditCrm(accountId) && !isExpiredLicense;

    return (
        <ProfileMain header={(
            <Item grow={1}>
                <Group justify="space-between" alignItems="center">
                    <Typography
                        fontWeight="semi-bold"
                        variant="heading-level-3">
                        CRM Configurations
                    </Typography>
                    {canEdit ? (
                        <Group>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={isActionDisabled}
                                onClick={() => {
                                    setRunDiscardChanges(true);
                                    accountSubmit({submit: false, place: 'CRM Configurations'});
                                }}>
                                Discard Changes
                            </Button>
                            <Button
                                busy={saveBusy}
                                variant="Type.PRIMARY"
                                disabled={isActionDisabled}
                                onClick={() => {
                                    setSaveBusy(true);
                                    setRunSaveChanges(true);
                                    accountSubmit({submit: true, place: 'CRM Configurations'});
                                }}>
                                Save Changes
                            </Button>
                        </Group>
                    ) : null}
                </Group>
            </Item>
        )}>
            <Table>
                <Head>
                    <Cell width={55}/>
                    <Cell>
                        <Group justify="space-between">
                            <Group>
                                <Item style={{width: 80}}>CRM</Item>
                                <Item style={{width: 100}}>
                                    <Group justify="end">
                                        Users
                                    </Group>
                                </Item>
                                <Item style={{width: 150}}>
                                    <Group justify="end">
                                        Imports & syncs
                                    </Group>
                                </Item>
                                <Item style={{width: 150}}>
                                    <Group justify="end">
                                        Import From PB
                                    </Group>
                                </Item>
                                <Item style={{maxWidth: 300}} shrink={1}>
                                    Connection status
                                </Item>
                            </Group>
                            <Item style={{width: 30}}/>
                        </Group>
                    </Cell>
                </Head>
                <Body>
                    {[
                        SalesforceRow,
                        MSDynamicsRow,
                        DealCloudRow,
                        HubspotRow,
                        PitchBookRow,
                    ].map((CrmComponent, i) => (
                        <CrmComponent
                            key={i}
                            {...{
                                accountId,
                                licenseId,
                                currentCrm: currentCrmAccordion,
                                runSaveChanges,
                                runDiscardChanges,
                                onChangeActiveCrm: setCurrentCrmAccordion,
                                onChangesSaved,
                                onChangesDiscarded: () => setRunDiscardChanges(false),
                                onActionDisabled: setActionDisabled,
                                editDisabled: !canEdit,
                            }}
                        />
                    ))}
                </Body>
            </Table>
        </ProfileMain>
    );
};
